/**
 * Task #5797 — "just created from checkout" marker.
 *
 * A request / no-payment booking (e.g. a Rest-of-World self-drive request that
 * charges nothing up front) creates its trip row synchronously in
 * POST /bookings, but a brief read-after-write lag can make the very first
 * GET /bookings/:publicId 404. To tell that transient race apart from a
 * genuinely missing booking (a stale bookmark, a foreign trip, a deleted
 * request), checkout stamps the freshly created trip's public_id here right
 * before navigating to the confirmation page. The confirmation page reads it: a
 * 404 on a *freshly stamped* id means "not materialized yet → retry", anything
 * else means "not found → error" (the historical behaviour).
 *
 * The marker is short-lived so it can never make an unrelated genuine 404 hang.
 */

const KEY = "bac_fresh_booking";
const TTL_MS = 2 * 60 * 1000;

interface FreshBookingMarker {
  publicId: string;
  at: number;
}

/** Stamp a freshly created booking's public_id right before navigating to it. */
export function markFreshBooking(publicId: string | null | undefined): void {
  if (!publicId) return;
  try {
    const payload: FreshBookingMarker = { publicId, at: Date.now() };
    sessionStorage.setItem(KEY, JSON.stringify(payload));
  } catch {
    /* noop — private mode / storage disabled. The confirmation-page retry is a
       safety net; losing the marker just means a non-fresh 404 fails fast. */
  }
}

/** True when `publicId` was stamped by checkout within the freshness window. */
export function isFreshBooking(publicId: string | null | undefined): boolean {
  if (!publicId) return false;
  try {
    const raw = sessionStorage.getItem(KEY);
    if (!raw) return false;
    const data = JSON.parse(raw) as Partial<FreshBookingMarker> | null;
    if (!data || data.publicId !== publicId) return false;
    if (typeof data.at !== "number" || Date.now() - data.at > TTL_MS) return false;
    return true;
  } catch {
    return false;
  }
}

/**
 * Clear the marker. When `publicId` is given, only clears it if the stored
 * marker is for that id (so a concurrent booking's marker isn't wiped).
 */
export function clearFreshBooking(publicId?: string | null): void {
  try {
    if (publicId) {
      const raw = sessionStorage.getItem(KEY);
      if (raw) {
        const data = JSON.parse(raw) as Partial<FreshBookingMarker> | null;
        if (data?.publicId && data.publicId !== publicId) return;
      }
    }
    sessionStorage.removeItem(KEY);
  } catch {
    /* noop */
  }
}
